import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import OurStory from "@/pages/OurStory";
import Experience from "@/pages/Experience";
import Collection from "@/pages/Collection";
import QualityStandard from "@/pages/QualityStandard";
import FarmToFamily from "@/pages/FarmToFamily";
import Sustainability from "@/pages/Sustainability";
import Academy from "@/pages/Academy";
import Faq from "@/pages/Faq";
import Cart from "@/pages/Cart";
import Product from "@/pages/Product";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import LegalPage from "@/pages/LegalPage";
import AccountLogin from "@/pages/AccountLogin";
import Account from "@/pages/Account";
import AccountCallback from "@/pages/AccountCallback";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import Products from "@/admin/Products";
import Coupons from "@/admin/Coupons";
import SettingsPage from "@/admin/SettingsPage";
import Faqs from "@/admin/Faqs";
import InboxPage from "@/admin/Inbox";
import Orders from "@/admin/Orders";
import Pages from "@/admin/Pages";
import Reviews from "@/admin/Reviews";
import Testimonials from "@/admin/Testimonials";
import Categories from "@/admin/Categories";
import CategoryHub from "@/pages/CategoryHub";
import CategoryLanding from "@/pages/CategoryLanding";
import HomeNew from "@/pages/HomeNew";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
};

const Shell = () => {
  useLenis();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <CustomerAuthProvider>
        <AccountCallback />
      </CustomerAuthProvider>
    );
  }
  return (
    <CustomerAuthProvider>
    <CartProvider>
      <ScrollToTop />
      <AnnouncementBar />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home-new" element={<HomeNew />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/experience" element={<Experience />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/shop-by-category" element={<CategoryHub />} />
          <Route path="/category/:slug" element={<CategoryLanding />} />
          <Route path="/quality-standard" element={<QualityStandard />} />
          <Route path="/farm-to-family" element={<FarmToFamily />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/promise" element={<Navigate to="/experience" replace />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/product/:id" element={<Product />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/account/login" element={<AccountLogin />} />
          <Route path="/account" element={<Account />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer />
      <WhatsAppButton />
    </CartProvider>
    </CustomerAuthProvider>
  );
};

const AdminShell = () => (
  <AdminAuthProvider>
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route element={<AdminLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="categories" element={<Categories />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="testimonials" element={<Testimonials />} />
        <Route path="orders" element={<Orders />} />
        <Route path="coupons" element={<Coupons />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="pages" element={<Pages />} />
        <Route path="faqs" element={<Faqs />} />
        <Route path="inbox" element={<InboxPage />} />
      </Route>
    </Routes>
  </AdminAuthProvider>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <Routes>
            <Route path="/admin/*" element={<AdminShell />} />
            <Route path="/*" element={<Shell />} />
          </Routes>
          <Toaster position="bottom-right" toastOptions={{ style: { background: "#13281C", color: "#F7F4EC", border: "1px solid rgba(176,141,62,0.35)" } }} />
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
